import { defineStore } from 'pinia';
import { LoginResponse } from '@/types/Responses/LoginResponse.ts';
import { Ref, ref } from 'vue';
import { OAuthClientResponse } from '@/types/Responses/OAuthClientResponse.ts';
import { LocationQueryValue, RouteLocationNormalized, useRouter } from 'vue-router';
import type { OAuthApproveResponse } from '@/types/Responses/OAuthApproveResponse.ts';
import { RedirectResponse } from '@/types/Responses/RedirectResponse.ts';
import { ForgotPasswordResponse } from '@/types/Responses/ForgotPasswordResponse.ts';
import { ResetPasswordResponse } from '@/types/Responses/ResetPasswordResponse.ts';
import { ForgotUsernameResponse } from '@/types/Responses/ForgotUsernameResponse.ts';
import Cookies from 'js-cookie';
import { useStorage } from '@vueuse/core';
import { useUserStore } from '@/stores/user.ts';
import { Api } from '@/utils/api.ts';
import { isNeedMfa, isUserBlocked, isUserOnboarding, LoginUnauthorizedResponse } from '@/types/Responses/LoginNeedMfaResponse.ts';
import { MfaMethod } from '@/types/MfaMethod.ts';
import { MfaActivateResponse } from '@/types/Responses/MfaActivateResponse.ts';
import { MessageChannel } from '@/types/MessageChannel.ts';
import { getCookieDomain } from '@/utils/cookieDomain.ts';

export const useAuthStore = defineStore('auth', () => {
	// Logged user
	const cookiePrefix = ['develop', 'staging'].includes(import.meta.env.VITE_ENV ?? '') ? `${import.meta.env.VITE_ENV}_` : '';
	const token: Ref<string> = ref(Cookies.get(`${cookiePrefix}uzzer_token`) || Cookies.get(`${cookiePrefix}uzzer_token_duplicated`) || '');
	const partnerToken: Ref<string> = ref(sessionStorage.getItem('partner_token') ?? '');
	const isLogged: Ref<boolean> = ref(token.value.length > 0);
	const isJustLogged: Ref<boolean> = ref(false); // Difference with isLogged is whether we were logged during this session or not
	const isDuplicatedLogged: Ref<boolean> = ref(Cookies.get('uzzer_token_duplicated') !== undefined);
	const isPartnerLogin: Ref<boolean> = ref(false);
	const isLux: Ref<boolean> = ref(window.location.host.includes('up-luxembourg'));
	const availableLanguages: string[] = isLux.value ? ['fr_LU', 'en_LU'] : ['fr', 'nl', 'en'];
	const defaultLocale = isLux.value ? 'fr_LU' : 'nl';
	// The country a phone field starts on; the field switches by itself when a number comes with its calling code
	const defaultPhoneCountry = isLux.value ? 'LU' : 'BE';
	const loginInputType: Ref<string> = ref('');
	//For redirect on consent
	const redirectUri: Ref<string> = ref('');
	const oauthState: Ref<string> = ref('');
	const fuzzionMfas: Ref<MfaMethod[]> = ref([]);

	// For Approval screen with OAuth scope acceptation
	const partnerData: Ref<any> = ref({});
	const TARGET_URL_KEY = 'auth_target_url';
	const targetUrl = useStorage<string>(TARGET_URL_KEY, '/', sessionStorage);

	//Forgot Password
	const showTabs: Ref<boolean> = ref(true);
	const forgotPasswordSent: Ref<boolean> = ref(false);
	const forgotPasswordDestination: Ref<string> = ref('');
	const forgotPasswordDestinationType: Ref<string> = ref('');
	const resetPasswordCode: Ref<number | null> = ref(null);

	// Forgot username
	const forgotUsernameInput: Ref<string> = ref('');
	const forgotUsernameInputType: Ref<string> = ref('');
	const forgotUsernameEmail: Ref<string | null> = ref(null);
	const forgotUsernamePhone: Ref<string | null> = ref(null);

	const router = useRouter();

	const getToken = async (requestBody: BodyInit): Promise<any> => {
		const data = {
			method: 'POST',
			headers: Api.commonHeaders,
			body: requestBody,
		};
		const url = Api.commonUrl + '/token';
		return new Promise((resolve, reject) => {
			fetch(url, data)
				.then(async (response) => {
					if (response.status === 401) {
						let body: LoginUnauthorizedResponse = await response.json();

						if (isNeedMfa(body)) {
							sessionStorage.setItem('loginPayload', body.mfa.payload);
							await router.push({
								name: 'mfa-methods',
								query: {
									isFuzzion: 'false',
									methods: JSON.stringify(body.mfa.methods),
									isLogin: 'true',
								},
							});
							throw 'Need MFA';
						} else if (isUserOnboarding(body)) {
							throw 'User onboarding';
						} else if (isUserBlocked(body)) {
							//TODO: handle other cases when backend will support other password status
							throw 'User blocked';
						}
					}
					if (response.status !== 200) {
						throw response;
					} else {
						return response.json();
					}
				})
				.then((result: LoginResponse) => {
					const urlParams = new URLSearchParams(targetUrl.value.split('?')[1]);
					const oauthClients: Array<string> = import.meta.env.VITE_CLIENT_ID.split(',');
					const clientId = urlParams.get('client_id');
					if (clientId) {
						isPartnerLogin.value = !oauthClients.includes(clientId);
					}
					loginByToken(result.access_token)
						.then((result) => {
							resolve(result);
						})
						.catch((response) => {
							reject(response);
						});
				})
				.catch((response) => {
					reject(response);
				});
		});
	};

	const loginByToken = async (access_token: string): Promise<void> => {
		const cookieDomain = getCookieDomain();
		token.value = access_token;

		let isWeak = false;
		try {
			await useUserStore().refreshUser();
		} catch {
			isWeak = true;
		}

		// Always finalize the login before signalling weak password,
		// so the user has a valid session for the password-change flow.
		Cookies.set(`${cookiePrefix}uzzer_token`, access_token, {
			expires: 30,
			sameSite: 'Lax',
			domain: cookieDomain,
		});
		isJustLogged.value = true;
		isLogged.value = true;

		if (isWeak) {
			throw 'weak';
		}
	};

	const removeSensitiveInformation = () => {
		const cookieDomain = getCookieDomain();

		const cookiesToken = Cookies.get(`${cookiePrefix}token`);
		if (cookiesToken) {
			Api.delete('/token', { token: cookiesToken });
		}

		const cookiesUzzerToken = Cookies.get(`${cookiePrefix}uzzer_token`);
		if (cookiesUzzerToken) {
			Api.delete('/token', { token: cookiesUzzerToken });
		}

		Cookies.remove(`${cookiePrefix}uzzer_token`, { domain: cookieDomain });
		Cookies.remove(`${cookiePrefix}uzzer_token_duplicated`, { domain: cookieDomain });
		Cookies.remove(`${cookiePrefix}token`, { domain: cookieDomain });
		token.value = '';
	};

	const logout = (): void => {
		removeSensitiveInformation();
		sessionStorage.clear();
		// clear() bypasses useStorage, which keeps the return URL in memory only and would not write it back when
		// it is set to the same value again: restore it so storage and memory agree, e.g. after a page reload.
		sessionStorage.setItem(TARGET_URL_KEY, targetUrl.value);
		isLogged.value = false;
		isJustLogged.value = false;
		isPartnerLogin.value = false;
		isDuplicatedLogged.value = false;
		try {
			useStorage('account', {}).value = null;
		} catch {
			// No localStorage -> no need to clean it
			console.info('Your local storage is not enabled');
		}
		useUserStore().clean();
	};

	const getAuthorize = async (
		client_id: string | null | LocationQueryValue[],
		redirect_uri: string | null | LocationQueryValue[],
		code_challenge?: string,
		code_challenge_method?: string,
	): Promise<string> => {
		Api.commonHeaders.Authorization = 'Bearer ' + token.value;
		const data = {
			method: 'GET',
			headers: Api.commonHeaders,
		};
		let url = Api.commonUrl + '/authorize?client_id=' + client_id + '&redirect_uri=' + redirect_uri + '&response_type=code';
		if (code_challenge) {
			url += '&code_challenge=' + encodeURIComponent(code_challenge);
			url += '&code_challenge_method=' + encodeURIComponent(code_challenge_method || 'S256');
		}
		return new Promise<string>((resolve, reject) => {
			fetch(url, data)
				.then(async (response) => {
					if (!response.ok) {
						throw await response.json();
					}
					return response.json() as Promise<OAuthClientResponse | OAuthApproveResponse>;
				})
				.then((result) => {
					if (result.approvalType === 'scopes') {
						partnerData.value = result;
						resolve('approve');
					} else {
						resolve(result.target);
					}
				})
				.catch((error) => reject(error));
		});
	};
	const sendApproval = async (method: string, requestBody: BodyInit) => {
		// can be a POST for approval or DELETE for refusal
		Api.commonHeaders.Authorization = 'Bearer ' + token.value;
		const data = {
			method: method,
			headers: Api.commonHeaders,
			body: requestBody,
			redirect: 'manual',
		};
		const url = Api.commonUrl + '/authorize';
		return fetch(url, data as any).then((response) => {
			if (response.status >= 400) {
				return response.json().then((error) => {
					throw error;
				});
			} else {
				return response.json();
			}
		});
	};

	const setTargetUrl = (to: RouteLocationNormalized): void => {
		targetUrl.value = to.fullPath;
	};

	const clearTargetUrl = (): void => {
		targetUrl.value = '/';
	};

	const storeForgotPasswordInfo = (data: ForgotPasswordResponse): void => {
		forgotPasswordSent.value = true;
		forgotPasswordDestination.value = data.recipient;
		forgotPasswordDestinationType.value =
			data.technology === 'EMAIL' ? 'email' : MessageChannel[data.technology as keyof typeof MessageChannel];
	};

	const sendForgotPassword = async (requestBody: BodyInit): Promise<boolean> => {
		const data = {
			method: 'POST',
			headers: Api.commonHeaders,
			body: requestBody,
		};
		const url = Api.commonUrl + '/user/forgot-password';
		return new Promise<any>((resolve) => {
			fetch(url, data as any)
				.then((response) => {
					return response.json();
				})
				.then((result: RedirectResponse) => resolve(result));
		});
	};

	const resetPassword = async (requestBody: BodyInit): Promise<ResetPasswordResponse> => {
		const data = {
			method: 'POST',
			headers: Api.commonHeaders,
			body: requestBody,
		};
		const url = Api.commonUrl + '/user/reset-password';
		return new Promise((resolve, reject) => {
			fetch(url, data)
				.then((response) => (response.ok ? response.json() : Promise.reject(response)))
				.then((result) => resolve(result))
				.catch((error) => reject(error));
		});
	};

	const storeForgotUsernameInfo = (data: ForgotUsernameResponse | null, pan: string | null, type: 'pan' | 'niss'): void => {
		forgotUsernameEmail.value = data ? data.email : null;
		forgotUsernamePhone.value = data ? data.phone : null;

		forgotUsernameInput.value = pan ?? '';
		forgotUsernameInputType.value = type;
	};

	const handleLoginMfa = async (type: string, value?: string, isEdit?: boolean, channel?: string, isDryRun?: boolean) => {
		const data = {
			method: 'GET',
			headers: Api.commonHeaders,
		};
		const url = new URL(Api.commonUrl + `/mfa/${type}/activate`);
		if (value) {
			url.searchParams.append('target', value);
		}
		if (isEdit) {
			url.searchParams.append('edit', '1');
		}
		if (channel) {
			url.searchParams.append('channel', channel);
		}
		if (isDryRun && !isLux.value) {
			url.searchParams.append('dry-run', '1');
		}
		const response = await fetch(url.toString(), data);
		if (response.status === 204) {
			return {
				skip: true,
			};
		}
		const result = await response.json();
		if (response.status === 422) {
			return Promise.reject({ errorMsg: result.message });
		}
		return {
			mfaPayload: result.payload,
			fuzzion: result.fuzzion,
			data: result.data,
		};
	};

	const handleConfirmMfa = async (mfaPayload: string, validationCode: string, target?: string, method?: string) => {
		type Body = {
			payload: string;
			code: string;
		};
		const body: Body = {
			payload: mfaPayload,
			code: validationCode,
		};

		const response = await Api.post('/mfa/activate', body);
		const result: MfaActivateResponse = await response.json();
		if (!response.ok) {
			return Promise.reject({ result });
		}
		if (result.fuzzion) {
			sessionStorage.setItem('fuzzion_payload', result.payload);
			router.push({ name: 'duplicated', query: { target, method } });
			return { fuzzion: true };
		}
	};

	const handleLoginUpdate = async (type: string, value: string) => {
		type Body = {
			email?: string;
			phone?: string;
		};

		const body: Body = {};
		if (type === 'email') {
			body.email = value;
		} else {
			body.phone = value;
		}

		const response = await Api.post('/user/login', body);
		const result = await response.json();
		if (response.status === 422) {
			return Promise.reject({ errorMsg: result.message });
		}
		return { payload: result.payload };
	};

	const handleFuzzion = async (code?: string, redirect?: string) => {
		const payload = sessionStorage.getItem('fuzzion_payload');
		if (!payload) {
			router.push(targetUrl.value);
			return;
		}
		const response = await Api.post('/fuzzion', { payload, code });
		const result = await response.json();
		if (!response.ok) {
			return;
		} else {
			if (result.mfa && result.mfa.length) {
				fuzzionMfas.value = result.mfa;
				router.push({ name: 'mfa-methods', query: { isFuzzion: 'true', redirect: 'fuzzion-success' } });
				return;
			} else {
				sessionStorage.removeItem('fuzzion_payload');
				useUserStore().user = result.data;
				await useUserStore().refreshMfa();
				router.replace({ name: redirect });
				return;
			}
		}
	};

	const handleFuzzionMfa = async (mfaId: number, target?: string) => {
		const payload = sessionStorage.getItem('fuzzion_payload');
		if (!payload) {
			router.push(targetUrl.value);
		}
		const response = await Api.post('/mfa/fuzzion', { payload, mfaId, target: mfaId < 0 ? target : undefined });
		const result = await response.json();
		if (response.status === 200) {
			sessionStorage.setItem('fuzzion_payload', result.payload);
			return;
		}
	};

	const handleLoginUpdateConfirm = async (payload: string, code: string) => {
		type Body = {
			payload: string;
			code: string;
		};

		const body: Body = {
			payload,
			code,
		};

		await Api.post('/user/login/confirm', body).catch((e) => {
			throw e;
		});
	};

	const linkAccount = async () => {
		const data = {
			method: 'POST',
			headers: Api.commonHeaders,
			body: {},
		};
		return await Api.post('/user/link-account', data).then(async (response) => {
			if (response.status !== 200) {
				throw response;
			} else {
				return response.json();
			}
		});
	};

	return {
		// DATA
		cookiePrefix,
		token,
		loginInputType,
		isLogged,
		isJustLogged,
		isPartnerLogin,
		isLux,
		availableLanguages,
		defaultLocale,
		defaultPhoneCountry,
		isDuplicatedLogged,
		partnerData,
		targetUrl,
		forgotPasswordSent,
		forgotPasswordDestination,
		forgotPasswordDestinationType,
		resetPasswordCode,
		forgotUsernameEmail,
		forgotUsernamePhone,
		forgotUsernameInput,
		forgotUsernameInputType,
		redirectUri,
		oauthState,
		fuzzionMfas,
		showTabs,
		partnerToken,

		// FUNCTIONS
		getToken,
		logout,
		getAuthorize,
		sendApproval,
		setTargetUrl,
		clearTargetUrl,
		storeForgotPasswordInfo,
		sendForgotPassword,
		resetPassword,
		storeForgotUsernameInfo,
		loginByToken,
		handleLoginMfa,
		handleConfirmMfa,
		handleFuzzion,
		handleFuzzionMfa,
		handleLoginUpdate,
		handleLoginUpdateConfirm,
		removeSensitiveInformation,
		linkAccount,
	};
});
